import React, { useState } from 'react';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import { object } from 'prop-types';
import { FaCheck, FaTimes, FaRegAddressBook } from 'react-icons/fa';
import FormControl from 'react-bootstrap/FormControl';
import Row from 'react-bootstrap/Row';
import FormGroup from 'react-bootstrap/FormGroup';
import FormLabel from 'react-bootstrap/FormLabel';
import Col from 'react-bootstrap/Col';
import { TextMaskCustom } from '../Patient/PatientProfile';
import { useForm, Controller } from 'react-hook-form';
import { chrApi } from '../../api/api';
import { useHistory } from 'react-router-dom';
import get from 'lodash/get';
import { useMutation } from 'react-query';
import Loader from 'react-loader';
import { useQMEByApptId } from './hooks';
import { toast } from 'react-toastify';
import useToggle from '../../hooks/useToggle';
import QMEExternalContactLookupPane from './QMEExternalContactLookupPane';

function QMEExternalContactsForm(props) {
  const [contactLookup, toggleContactLookup] = useToggle(false);
  const [activeLookupType, setActiveLookupType] = useState('');

  const [existingContact, setExistingContact] = useState({ da: '', adj: '', aa: '' });

  const history = useHistory();
  const appointmentId = get(props, 'match.params.appointmentId');

  const { control, reset, handleSubmit, setValue, errors, getValues } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      adjusterDetails: {
        id: '',
        companyName: '',
        name: '',
        phone: '',
        fax: '',
        email: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
      },
      daDetails: {
        id: '',
        firmName: '',
        name: '',
        phone: '',
        fax: '',
        email: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
      },
      aaDetails: {
        id: '',
        firmName: '',
        name: '',
        phone: '',
        fax: '',
        email: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
      },
    },
  });

  const sendBackToQME = () => {
    history.push(`/qme_appointment/${appointmentId}/`);
  };

  const { data, isLoading } = useQMEByApptId(appointmentId, (res) => {
    const { adjusterDetails = {}, daDetails = {}, aaDetails = {} } = res;
    reset({ adjusterDetails, daDetails, aaDetails });
    setExistingContact({ da: daDetails.id, aa: aaDetails.id, adj: adjusterDetails.id });
  });

  const qmeId = data?.id;

  const { mutate } = useMutation(
    (formData) => {
      if (qmeId) return chrApi('put', `medical_evaluation/${qmeId}`, { ...formData, id: qmeId });
    },
    {
      mutationKey: 'updateExternalContacts',
      onSuccess: () => {
        toast.success('External Contacts Successfully Updated');
        sendBackToQME();
      },
    },
  );

  function onSubmit(formData) {
    // console.log(existingContact);
    if (existingContact.aa) formData.aaDetails.id = existingContact.aa;
    if (existingContact.da) formData.daDetails.id = existingContact.da;
    if (existingContact.adj) formData.adjusterDetails.id = existingContact.adj;
    mutate(formData);
  }

  function handleOnCancel() {
    sendBackToQME();
  }

  if (isLoading) {
    return <Loader loaded={false} />;
  }

  // console.log(errors);
  return (
    <Container fluid>
      <p className={'h4 mb-3'}>QME-AME External Contacts Info</p>
      <Container fluid className={'bg-white p-3'}>
        <div className={'align-items-center d-flex mb-3'}>
          <p className={'h5 mb-0'}>Adjuster Information</p>
          <FaRegAddressBook
            fontSize={20}
            className={'ml-2 csr-pointer'}
            onClick={() => {
              setActiveLookupType('Adjuster');
              toggleContactLookup();
            }}
          />
        </div>
        <Row>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.companyName'}>
              <FormLabel>{'Company Name'}</FormLabel>
              <Controller
                name={'adjusterDetails.companyName'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.firstName'}>
              <FormLabel>{'ADJ First Name'}</FormLabel>
              <Controller
                name={'adjusterDetails.firstName'}
                rules={{ required: true }}
                control={control}
                defaultValue={''}
                render={({ onChange, value }, { invalid }) => <FormControl isInvalid={invalid} onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.lastName'}>
              <FormLabel>{'ADJ Last Name'}</FormLabel>
              <Controller
                name={'adjusterDetails.lastName'}
                rules={{ required: true }}
                control={control}
                defaultValue={''}
                render={({ onChange, value }, { invalid }) => <FormControl isInvalid={invalid} onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.address.street'}>
              <FormLabel>{'Address'}</FormLabel>
              <Controller
                name={'adjusterDetails.address.street'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.address.city'}>
              <FormLabel>{'City'}</FormLabel>
              <Controller
                name={'adjusterDetails.address.city'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.address.state'}>
              <FormLabel>{'State'}</FormLabel>
              <Controller
                name={'adjusterDetails.address.state'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.address.zip'}>
              <FormLabel>{'Zip'}</FormLabel>
              <Controller
                name={'adjusterDetails.address.zip'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'number'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormLabel>Phone</FormLabel>
            <Controller
              defaultValue={''}
              control={control}
              as={<TextMaskCustom isInvalid={errors?.adjusterDetails?.phone} />}
              rules={{
                validate: (value) => !value || value.length === 10,
              }}
              name={'adjusterDetails.phone'}
            />
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.fax'}>
              <FormLabel>{'Fax'}</FormLabel>
              <Controller
                name={'adjusterDetails.fax'}
                rules={{
                  validate: (value) => !value || value.length === 10,
                }}
                control={control}
                defaultValue={''}
                as={<TextMaskCustom isInvalid={errors?.adjusterDetails?.fax} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'adjusterDetails.email'}>
              <FormLabel>{'Email'}</FormLabel>
              <Controller
                name={'adjusterDetails.email'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <hr />
        <div className={'align-items-center d-flex mb-3'}>
          <p className={'h5 mb-0'}>Defense Attorney Information</p>
          <FaRegAddressBook
            fontSize={20}
            className={'ml-2 csr-pointer'}
            onClick={() => {
              setActiveLookupType('DefenseAttorney');
              toggleContactLookup();
            }}
          />
        </div>

        <Row>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.firmName'}>
              <FormLabel>{'Firm Name'}</FormLabel>
              <Controller
                name={'daDetails.firmName'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.firstName'}>
              <FormLabel>{'First Name'}</FormLabel>
              <Controller
                name={'daDetails.firstName'}
                control={control}
                defaultValue={''}
                rules={{ required: true }}
                render={({ onChange, value }, { invalid }) => <FormControl isInvalid={invalid} onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.lastName'}>
              <FormLabel>{'Last Name'}</FormLabel>
              <Controller
                name={'daDetails.lastName'}
                control={control}
                defaultValue={''}
                rules={{ required: true }}
                render={({ onChange, value }, { invalid }) => <FormControl isInvalid={invalid} onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.address.street'}>
              <FormLabel>{'Address'}</FormLabel>
              <Controller
                name={'daDetails.address.street'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.address.city'}>
              <FormLabel>{'City'}</FormLabel>
              <Controller
                name={'daDetails.address.city'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.address.state'}>
              <FormLabel>{'State'}</FormLabel>
              <Controller
                name={'daDetails.address.state'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.address.zip'}>
              <FormLabel>{'Zip'}</FormLabel>
              <Controller
                name={'daDetails.address.zip'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'number'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormLabel>Phone</FormLabel>
            <Controller
              defaultValue={''}
              control={control}
              rules={{
                validate: (value) => !value || value.length === 10,
              }}
              as={<TextMaskCustom isInvalid={errors?.daDetails?.phone} />}
              name={'daDetails.phone'}
            />
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.fax'}>
              <FormLabel>{'Fax'}</FormLabel>
              <Controller
                name={'daDetails.fax'}
                rules={{
                  validate: (value) => !value || value.length === 10,
                }}
                control={control}
                defaultValue={''}
                as={<TextMaskCustom isInvalid={errors?.daDetails?.fax} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'daDetails.email'}>
              <FormLabel>{'Email'}</FormLabel>
              <Controller
                name={'daDetails.email'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <hr />
        <div className={'align-items-center d-flex mb-3'}>
          <p className={'h5 mb-0'}>Applicant Attorney Information </p>
          <FaRegAddressBook
            fontSize={20}
            className={'ml-2 csr-pointer'}
            onClick={() => {
              setActiveLookupType('ApplicantAttorney');
              toggleContactLookup();
            }}
          />
        </div>

        <Row>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.firmName'}>
              <FormLabel>{'Firm Name'}</FormLabel>
              <Controller
                name={'aaDetails.firmName'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.firstName'}>
              <FormLabel>{'First Name'}</FormLabel>
              <Controller
                name={'aaDetails.firstName'}
                control={control}
                defaultValue={''}
                rules={{ required: true }}
                render={({ onChange, value }, { invalid }) => <FormControl isInvalid={invalid} onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.lastName'}>
              <FormLabel>{'Last Name'}</FormLabel>
              <Controller
                name={'aaDetails.lastName'}
                control={control}
                defaultValue={''}
                rules={{ required: true }}
                render={({ onChange, value }, { invalid }) => <FormControl isInvalid={invalid} onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.address.street'}>
              <FormLabel>{'Address'}</FormLabel>
              <Controller
                name={'aaDetails.address.street'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.address.city'}>
              <FormLabel>{'City'}</FormLabel>
              <Controller
                name={'aaDetails.address.city'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.address.state'}>
              <FormLabel>{'State'}</FormLabel>
              <Controller
                name={'aaDetails.address.state'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.address.zip'}>
              <FormLabel>{'Zip'}</FormLabel>
              <Controller
                name={'aaDetails.address.zip'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'number'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormLabel>Phone</FormLabel>
            <Controller
              defaultValue={''}
              control={control}
              rules={{
                validate: (value) => !value || value.length === 10,
              }}
              as={<TextMaskCustom isInvalid={errors?.aaDetails?.phone} />}
              name={'aaDetails.phone'}
            />
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.fax'}>
              <FormLabel>{'Fax'}</FormLabel>
              <Controller
                name={'aaDetails.fax'}
                rules={{
                  validate: (value) => !value || value.length === 10,
                }}
                control={control}
                defaultValue={''}
                as={<TextMaskCustom isInvalid={errors?.aaDetails?.fax} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'aaDetails.email'}>
              <FormLabel>{'Email'}</FormLabel>
              <Controller
                name={'aaDetails.email'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className={'my-3'}>
          <Col>
            <Button size={'sm'} onClick={handleSubmit(onSubmit)} className={'mr-2'} variant={'primary'}>
              <FaCheck className={'mr-1 mb-1'} />
              Save
            </Button>
            <Button onClick={handleOnCancel} size={'sm'} variant={'fff'}>
              <FaTimes className={'mr-1 mb-1'} />
              Cancel
            </Button>
          </Col>
        </Row>
      </Container>
      <QMEExternalContactLookupPane
        contactType={activeLookupType}
        userId={data?.providerId}
        isOpen={contactLookup}
        onClose={toggleContactLookup}
        onSelect={({ contactType, selectedContact }) => {
          const { id } = selectedContact;
          switch (contactType) {
            case 'ApplicantAttorney':
              setValue('aaDetails', selectedContact);
              setExistingContact((existingContact) => ({ ...existingContact, aa: id }));
              break;
            case 'DefenseAttorney':
              setValue('daDetails', selectedContact);
              setExistingContact((existingContact) => ({ ...existingContact, da: id }));

              break;
            case 'Adjuster':
              setValue('adjusterDetails', selectedContact);
              setExistingContact((existingContact) => ({ ...existingContact, adj: id }));
              break;
          }
        }}
      />
    </Container>
  );
}

QMEExternalContactsForm.propTypes = {
  adjusterDetails: object,
  daDetails: object,
  aaDetails: object,
};
QMEExternalContactsForm.defaultProps = {};

export default QMEExternalContactsForm;
